/**
 * Training: Volumen, geschätztes Maximum, Gewichtsvorschlag.
 *
 * Alles Schätzformeln — sie taugen zum Vergleich mit dir selbst über Wochen, nicht
 * als absolute Wahrheit. Die App zeigt sie deshalb als Verlauf, nicht als Urteil.
 */

import { isoDateInZone } from './time.js';
import type { IsoDate } from './time.js';
import type { TrackingMode } from './exercises.js';

export interface WorkoutSet {
  weightKg: number;
  reps: number;
}

/** Tonnage eines Satzes: Gewicht × Wiederholungen. */
export function setVolume(set: WorkoutSet): number {
  return set.weightKg * set.reps;
}

export function totalVolume(sets: readonly WorkoutSet[]): number {
  return sets.reduce((sum, set) => sum + setVolume(set), 0);
}

/**
 * Geschätztes Ein-Wiederholungs-Maximum nach Epley.
 * Ab etwa 12 Wiederholungen wird die Schätzung deutlich unzuverlässig — deshalb
 * gibt die Funktion oberhalb davon `null` statt einer Zahl, die seriös aussieht.
 */
export function estimatedOneRepMax(set: WorkoutSet): number | null {
  if (set.weightKg <= 0 || set.reps <= 0) return null;
  if (set.reps > 12) return null;
  if (set.reps === 1) return set.weightKg;
  return set.weightKg * (1 + set.reps / 30);
}

/** Bester geschätzter Maximalwert aus mehreren Sätzen. */
export function bestOneRepMax(sets: readonly WorkoutSet[]): number | null {
  let best: number | null = null;
  for (const set of sets) {
    const estimate = estimatedOneRepMax(set);
    if (estimate !== null && (best === null || estimate > best)) best = estimate;
  }
  return best;
}

export interface RepRange {
  min: number;
  max: number;
}

export interface ProgressionSuggestion {
  /** Vorgeschlagenes Gewicht für das nächste Mal. */
  weightKg: number;
  /** Warum — wird dem Nutzer angezeigt, damit der Vorschlag nachvollziehbar ist. */
  reason: 'increase' | 'hold' | 'deload';
  incrementKg: number;
}

/**
 * Gewichtsvorschlag nach der Regel aus dem Fachkonzept: Wenn **alle** Sätze am
 * oberen Ende der Wiederholungsspanne lagen, nächstes Mal erhöhen.
 *
 * Kein automatisches Reduzieren nach einem schwachen Tag — ein schlechter Tag hat
 * meist eine Ursache außerhalb des Trainings (zu wenig gegessen, zu wenig
 * geschlafen). Erst zwei Einheiten unterhalb der Spanne lösen einen Deload aus.
 */
export function suggestNextWeight(
  recentSessions: readonly (readonly WorkoutSet[])[],
  range: RepRange,
  incrementKg = 2.5,
): ProgressionSuggestion | null {
  const lastSession = recentSessions.at(-1);
  if (!lastSession || lastSession.length === 0) return null;

  const workingWeight = Math.max(...lastSession.map((set) => set.weightKg));
  if (!Number.isFinite(workingWeight) || workingWeight <= 0) return null;

  const allAtTop = lastSession.every((set) => set.reps >= range.max);
  if (allAtTop) {
    return { weightKg: workingWeight + incrementKg, reason: 'increase', incrementKg };
  }

  const belowRange = (session: readonly WorkoutSet[]): boolean =>
    session.length > 0 && session.every((set) => set.reps < range.min);

  const lastTwo = recentSessions.slice(-2);
  if (lastTwo.length === 2 && lastTwo.every(belowRange)) {
    const reduced = Math.max(0, roundToIncrement(workingWeight * 0.9, incrementKg));
    return { weightKg: reduced, reason: 'deload', incrementKg };
  }

  return { weightKg: workingWeight, reason: 'hold', incrementKg };
}

/** Auf die kleinste sinnvolle Hantelscheibe runden. */
export function roundToIncrement(weightKg: number, incrementKg: number): number {
  if (incrementKg <= 0) return weightKg;
  return Math.round(weightKg / incrementKg) * incrementKg;
}

// --- Sätze, wie sie wirklich geloggt werden ----------------------------------

/**
 * Ein aufgezeichneter Satz.
 *
 * Alle Felder optional, weil je nach Aufzeichnungsart andere gefüllt sind — die
 * Auswertung darunter entscheidet anhand der `TrackingMode`, welche sie liest,
 * statt fehlende Werte stillschweigend als 0 zu behandeln.
 *
 * `bodyWeightKg` wird **beim Satz** mitgeschrieben und nicht aus dem Profil
 * nachgeschlagen: Sonst würde jede alte Klimmzug-Schätzung falsch, sobald sich
 * das Körpergewicht ändert.
 */
export interface LoggedSet {
  weightKg?: number | null;
  reps?: number | null;
  seconds?: number | null;
  distanceM?: number | null;
  bodyWeightKg?: number | null;
  isWarmup?: boolean;
}

/**
 * Die Last, die dieser Satz wirklich bewegt hat — inklusive Körpergewicht, wo es
 * dazugehört. `null` heißt: **nicht bekannt**, nicht „null Kilo".
 */
export function totalLoadKg(set: LoggedSet, mode: TrackingMode): number | null {
  const added = set.weightKg ?? 0;
  const body = set.bodyWeightKg ?? null;

  switch (mode) {
    case 'weightReps':
      return set.weightKg !== null && set.weightKg !== undefined && set.weightKg > 0
        ? set.weightKg
        : null;
    case 'bodyweightReps':
      return body;
    case 'weightedBodyweight':
      return body === null ? null : body + added;
    case 'timeHold':
    case 'distanceTime':
      return null;
  }
}

/**
 * Volumen eines Satzes in Kilogramm, oder `null`, wenn es für diese Art keines
 * gibt. Eine Planke und ein Lauf haben kein Volumen in Kilogramm — sie mit 0 zu
 * verbuchen wäre eine Antwort, wo keine ist.
 */
export function loadVolumeOfSet(set: LoggedSet, mode: TrackingMode): number | null {
  if (set.isWarmup) return 0;
  const load = totalLoadKg(set, mode);
  const reps = set.reps ?? null;
  if (load === null || reps === null || reps <= 0) return null;
  return load * reps;
}

/** Geschätztes Maximum unter Berücksichtigung des Körpergewichts. */
export function estimatedOneRepMaxOf(set: LoggedSet, mode: TrackingMode): number | null {
  if (set.isWarmup) return null;
  const load = totalLoadKg(set, mode);
  const reps = set.reps ?? null;
  if (load === null || reps === null) return null;
  return estimatedOneRepMax({ weightKg: load, reps });
}

export interface VolumeEntry {
  set: LoggedSet;
  mode: TrackingMode;
}

export interface VolumeSummary {
  /** Summe in Kilogramm über alle Sätze, die eine Last haben. */
  kg: number;
  /** Wie viele Sätze eingerechnet wurden. */
  countedSets: number;
  /**
   * Sätze ohne vergleichbares Volumen — Zeit, Strecke, fehlendes Körpergewicht.
   *
   * Wird **angezeigt**, nicht verschwiegen: Eine Wochensumme, die zwei Läufe
   * unterschlägt, sieht aus wie eine vollständige Antwort.
   */
  uncountedSets: number;
}

export function summarizeVolume(entries: readonly VolumeEntry[]): VolumeSummary {
  let kg = 0;
  let countedSets = 0;
  let uncountedSets = 0;

  for (const entry of entries) {
    if (entry.set.isWarmup) continue;
    const volume = loadVolumeOfSet(entry.set, entry.mode);
    if (volume === null) uncountedSets += 1;
    else {
      kg += volume;
      countedSets += 1;
    }
  }

  return { kg, countedSets, uncountedSets };
}

/**
 * Gewichtsvorschlag direkt aus geloggten Sätzen.
 *
 * Aufwärmsätze fliegen raus — sonst gilt der leichte erste Satz als „unterhalb
 * der Spanne" und löst irgendwann einen Deload aus, den niemand verdient hat.
 * Für Zeit- und Streckenübungen gibt es keinen Vorschlag; `null` ist hier die
 * ehrliche Antwort.
 */
export function suggestFromLoggedSets(
  recentSessions: readonly (readonly LoggedSet[])[],
  mode: TrackingMode,
  range: RepRange,
  incrementKg = 2.5,
): ProgressionSuggestion | null {
  if (mode === 'timeHold' || mode === 'distanceTime') return null;

  const sessions = recentSessions
    .map((session) =>
      session
        .filter((set) => !set.isWarmup && (set.reps ?? 0) > 0)
        .map((set) => ({
          weightKg: mode === 'weightReps' ? (set.weightKg ?? 0) : (totalLoadKg(set, mode) ?? 0),
          reps: set.reps ?? 0,
        })),
    )
    .filter((session) => session.length > 0);

  if (sessions.length === 0) return null;
  return suggestNextWeight(sessions, range, incrementKg);
}

// --- Woche und Beständigkeit -------------------------------------------------

/**
 * Montag der Woche, in der dieser Zeitpunkt **in der Zeitzone des Nutzers**
 * liegt. Der Wochenschlüssel ist damit derselbe, egal ob die App in Berlin oder
 * der Scheduler in UTC rechnet.
 */
export function weekStart(instant: Date, timeZone: string): IsoDate {
  const date = isoDateInZone(instant, timeZone);
  const [y, m, d] = date.split('-').map(Number) as [number, number, number];
  const asUtc = new Date(Date.UTC(y, m - 1, d));
  // getUTCDay: 0 = Sonntag. ISO-Woche beginnt am Montag.
  const shift = (asUtc.getUTCDay() + 6) % 7;
  asUtc.setUTCDate(asUtc.getUTCDate() - shift);
  return asUtc.toISOString().slice(0, 10);
}

function previousWeek(week: IsoDate): IsoDate {
  const [y, m, d] = week.split('-').map(Number) as [number, number, number];
  return new Date(Date.UTC(y, m - 1, d - 7)).toISOString().slice(0, 10);
}

/**
 * Wie viele Wochen in Folge mindestens eine Einheit stattgefunden hat.
 *
 * Die laufende Woche zählt nur mit, wenn in ihr schon trainiert wurde —
 * andernfalls beginnt die Zählung in der Vorwoche. Sonst stünde die Serie
 * jeden Montagmorgen auf null, und die App würde eine Beständigkeit bestreiten,
 * die es gibt.
 *
 * Das Fachkonzept misst bewusst zuerst Konsistenz, nicht Körperfett: Das ist
 * das, was in Woche 3 überhaupt beeinflussbar ist.
 */
export function weeklyStreak(
  sessionInstants: readonly Date[],
  timeZone: string,
  now: Date = new Date(),
): number {
  if (sessionInstants.length === 0) return 0;

  const weeks = new Set(sessionInstants.map((instant) => weekStart(instant, timeZone)));
  const current = weekStart(now, timeZone);

  let cursor = weeks.has(current) ? current : previousWeek(current);
  let streak = 0;

  while (weeks.has(cursor)) {
    streak += 1;
    cursor = previousWeek(cursor);
  }

  return streak;
}

/**
 * Zusatzübungen wöchentlich durchrotieren.
 *
 * Aus dem Fachkonzept: Ohne Rotation bekommt immer dieselbe Übung die letzte,
 * müdeste Position — und wächst dann als Einzige nicht. Die Grundübung bleibt
 * vorn, deshalb rotiert nur der übergebene Rest.
 */
export function rotateAccessories<T>(items: readonly T[], weekIndex: number): T[] {
  if (items.length < 2) return [...items];
  const offset = ((weekIndex % items.length) + items.length) % items.length;
  return [...items.slice(offset), ...items.slice(0, offset)];
}

/**
 * Supersätze (23.10.2026): Übungen, die im Plan „mit der vorigen“ verbunden
 * sind, werden abwechselnd trainiert — A1, B1, Pause, A2, B2, Pause.
 *
 * Ein Block ist eine Übung allein oder eine Kette verbundener. Die Marke an
 * der ersten Übung des Tages zählt nicht: Davor gibt es nichts zu verbinden.
 */
export function supersetBlocks<T extends { supersetWithPrevious?: boolean }>(
  items: readonly T[],
): T[][] {
  const blocks: T[][] = [];
  for (const item of items) {
    const last = blocks.at(-1);
    if (last && item.supersetWithPrevious) last.push(item);
    else blocks.push([item]);
  }
  return blocks;
}

/**
 * Reihenfolge eines Trainingstags: Blöcke mit Grundübung vorn in Planfolge,
 * die übrigen rotieren wöchentlich — **als Block**. Einzeln rotiert, risse
 * die Rotation Supersätze auseinander.
 */
export function orderPlanBlocks<T extends { supersetWithPrevious?: boolean; isCompound: boolean }>(
  items: readonly T[],
  weekIndex: number,
): T[][] {
  const blocks = supersetBlocks(items);
  const compound = blocks.filter((block) => block.some((item) => item.isCompound));
  const accessory = blocks.filter((block) => !block.some((item) => item.isCompound));
  return [...compound, ...rotateAccessories(accessory, weekIndex)];
}

/**
 * Nach einem Satz im Supersatz: Geht es ohne Pause weiter, und womit?
 *
 * Weiter geht es mit der nächsten Übung des Blocks, die in dieser Runde noch
 * offen ist (weniger Sätze als die eben trainierte und nicht fertig). Gibt es
 * keine, ist die Runde durch — `null` heißt: jetzt Pause.
 */
export function supersetNext(
  block: readonly { id: string; sets: number }[],
  done: ReadonlyMap<string, number>,
  current: string,
): string | null {
  const index = block.findIndex((member) => member.id === current);
  if (index < 0) return null;
  const round = done.get(current) ?? 0;
  const next = block
    .slice(index + 1)
    .find((member) => (done.get(member.id) ?? 0) < Math.min(round, member.sets));
  return next?.id ?? null;
}

/**
 * Wochennummer seit einem festen Bezugspunkt — als Eingabe für `rotateAccessories`.
 * Bezug ist der Montag der Woche, in der der Plan angelegt wurde.
 */
export function weeksSince(start: Date, now: Date, timeZone: string): number {
  const from = weekStart(start, timeZone);
  const to = weekStart(now, timeZone);
  const diff = Date.parse(to) - Date.parse(from);
  return Math.max(0, Math.round(diff / (7 * 24 * 60 * 60 * 1000)));
}

/**
 * Bestes geschätztes Maximum je Trainingstag (Ortszeit), älteste zuerst — für
 * den Verlauf je Übung. Ein Tag mit fünf Sätzen ist ein Punkt, nicht fünf.
 */
export function bestPerDay(
  estimates: readonly { at: string; value: number }[],
  timeZone: string,
): { day: string; kg: number }[] {
  const best = new Map<string, number>();
  for (const estimate of estimates) {
    if (!estimate.at) continue;
    const day = isoDateInZone(new Date(estimate.at), timeZone);
    best.set(day, Math.max(best.get(day) ?? 0, estimate.value));
  }
  return [...best.entries()]
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([day, kg]) => ({ day, kg: Math.round(kg * 10) / 10 }));
}

/** Ab wann eine offene Einheit als vergessen gilt (Stunden seit Beginn). */
export const FORGOTTEN_SESSION_HOURS = 12;

/** Ohne einen einzigen Satz: so lange, wie eine Einheit im Mittel dauert. */
export const FORGOTTEN_SESSION_FALLBACK_MIN = 60;

export function isForgottenSession(startedAt: string, now: Date = new Date()): boolean {
  return now.getTime() - new Date(startedAt).getTime() > FORGOTTEN_SESSION_HOURS * 3_600_000;
}

/**
 * Ende einer vergessenen Einheit: der letzte erledigte Satz, sonst Beginn plus
 * eine Stunde — **nie „jetzt“**. Sonst dauerte die Einheit sechs Tage, und die
 * Energiebilanz zählte sechs Tage Krafttraining.
 */
export function forgottenSessionEnd(
  startedAt: string,
  completedAt: readonly (string | null)[],
): string {
  const start = new Date(startedAt).getTime();
  const last = completedAt
    .filter((value): value is string => value !== null)
    .map((value) => new Date(value).getTime())
    .filter((value) => value >= start)
    .reduce((max, value) => Math.max(max, value), Number.NEGATIVE_INFINITY);
  const end = Number.isFinite(last) ? last : start + FORGOTTEN_SESSION_FALLBACK_MIN * 60_000;
  return new Date(end).toISOString();
}
